/**
 * “内容使用说明”的内容本身（DATA-USE-COPY-01，c84a REPLY-I-20261009-31 第 29 项、-32 05:46Z 补充裁定；I 10-09 派、6c2b 10-09 定第二版）。
 * - 详情页（DataUsePage，/settings/data-use）放完整的三节：DataUseContent。
 * - 上传照片处原地打开的底部面板（DataUseNote 的 PhotoDataUseNote）按场合只放「照片」一节（DATA-USE-COPY-01-R1，I 10-09 收包意见）：
 *   PhotoDataUseContent——和详情页同一个「照片」一节组件（PhotoSection，面板用简版：省掉旅途那一句，第二版 I 10-09）、
 *   只取图像两种用途（images、photo_check）的「目前在用」，
 *   最后一行用文字指路到设置里的完整说明，不放跳转链接（点了会离开当前页，填好的表单会丢）。
 *
 * 三节：
 * 1. 交流内容：c84a 原句（按全站惯例 TA 两边带空格，6c2b 10-09 定），再写全哪些话走同一条路——
 *    私聊回信、主动来信、明信片上的话、旅行手账的措辞、家庭频道、接待时的对话、TA 自己想去哪儿的旅行心愿、TA 自己拿主意时的思考与参考叮嘱。
 *    出处：契约 SettingsView.model_replies 的说明、原来设置页那段说明（ModelRepliesSetting，DATA-USE-COPY-01 删掉的死代码）；
 *    接待：I107-W4 以后接待用不用语言服务只看运营能力 model_replies（不看前端传的 use_model，后端 test_i107_reception_ops_gate 钉着），
 *    运营一打开，接待里主人写的话就交给外部语言服务（6c2b 10-09 转 I）。data-services 里 conversation 那一项覆盖的就是这些。
 * 2. 照片：上传处那一句，再写用在哪里——放照片时先检查能不能用、TA 在星球上的样子、证件照；旅途中、聊天里的照片照着 TA 的样子准备
 *    （旅途那一句只在详情页，上传处的面板用简版省掉）。
 * 3. 目前在用的外部服务：读 GET /meta/data-services（dataServices.ts；**等 I108 契约同步换掉**手写的类型；回包只认 { services: [...] }）。
 *    只列接口里有的用途；名字为空就写“交给外部服务处理”；名字照后端原样显示、不过滤（设置里说明页上的品牌专名，6c2b 10-09 定）；
 *    读不到照实说读不到（固定文字照常显示，不编服务名）；接口给的列表是空的（面板里：图像两种用途一个都没有）就整块不显示。合同主体不显示。
 *
 * 不写：地域、保存多久、“不用于训练”这类承诺（没有出处）；“TA 的资料也会交出去”（6c2b 10-09 定不写）；AI 字样、技术词；品牌不进标题和按钮；
 * 也不写“上传”（这份内容会在形象页上原地打开，那一页整页不出现“上传”）、不写“画”。
 */
import { useQuery } from "@tanstack/react-query";
import { useServices } from "@/shared/services/registry";
import { Button, Card } from "@/shared/ui";
import { DATA_SERVICES_KEY, IMAGE_PURPOSES, readDataServices, type DataServicePurpose } from "./dataServices";
import "./dataUse.css";

/** c84a 原句（私聊提示、设置页隐私说明第一句、说明页交流内容第一句）；后面跟“查看内容使用说明” */
export const CHAT_DATA_USE = "为帮助 TA 理解和回应，与你交流的相关内容会交给外部语言服务处理。";
/** 上传照片的地方（建宠首只、再添一只、形象页补照片、换照片）和说明页照片一节第一句（6c2b 10-09 定） */
export const PHOTO_DATA_USE = "你放上来的照片会交给外部图像服务处理，用来检查照片和准备 TA 的样子。";
export const DATA_USE_TITLE = "内容使用说明";
export const DATA_USE_LEAD = "为了让 TA 理解你、回应你，也为了准备 TA 的样子和照片，有些内容会交给外部服务处理。这里写清是哪些内容、用在哪里。";
export const CHAT_SCOPE_NOTE = "包括你们聊的话和你确认过的叮嘱。下面这些都走同一条路：";
/** 交流内容走同一条路的地方（按契约 SettingsView.model_replies 的说明和 I107-W4 的接待口径写，别漏也别编） */
export const CHAT_SURFACES = [
  "TA 在私聊里给你的回信",
  "TA 主动给你的来信",
  "明信片上的话",
  "旅行手账的措辞",
  "家庭频道里 TA 说的话",
  "接待时你和接待员的对话",
  "TA 自己想去哪儿的旅行心愿",
  "星球上 TA 自己拿主意时，怎么想、怎么参考你的叮嘱",
] as const;
export const PHOTO_SCOPE_NOTE = "用在这些地方：";
export const PHOTO_SURFACES = ["放照片时，先检查照片能不能用", "TA 在星球上的样子", "TA 的证件照"] as const;
export const PHOTO_TRIP_NOTE = "TA 在旅途中、在聊天里给你的照片，也交给外部图像服务，照着 TA 在星球上的样子来准备。";
export const SERVICES_TITLE = "目前在用的外部服务";
export const SERVICES_UNREADABLE = "目前在用的服务暂时读不到，稍后再来看看。";
/** 原地打开的面板最后一行：用文字指路，不放跳转链接（I 10-09，DATA-USE-COPY-01-R1） */
export const FULL_DATA_USE_HINT = "完整的内容使用说明在 设置 › 内容使用说明";

/** 完整的说明：详情页用 */
export function DataUseContent() {
  return (
    <div className="ps-stack ps-data-use-content" data-testid="data-use-content">
      <p className="ps-data-use__lead">{DATA_USE_LEAD}</p>
      <ChatSection />
      <PhotoSection variant="full" />
      <InUseServices />
    </div>
  );
}

/** 上传照片处原地打开的面板：只放「照片」一节（简版，没有旅途那一句）、图像两种用途的「目前在用」，最后一行文字指路（没有链接） */
export function PhotoDataUseContent() {
  return (
    <div className="ps-stack ps-data-use-content" data-testid="photo-data-use-content">
      <PhotoSection variant="brief" />
      <InUseServices only={IMAGE_PURPOSES} />
      <p className="ps-data-use__more">{FULL_DATA_USE_HINT}</p>
    </div>
  );
}

/** 「交流内容」一节 */
function ChatSection() {
  return (
    <Card data-testid="data-use-chat">
      <h2 className="ps-section-title">交流内容</h2>
      <p>{CHAT_DATA_USE}</p>
      <p>{CHAT_SCOPE_NOTE}</p>
      <ul className="ps-data-use__list" aria-label="交流内容用在这些地方">
        {CHAT_SURFACES.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </Card>
  );
}

/**
 * 「照片」一节：详情页、上传处的面板共用这一个组件，按场合分两种（DATA-USE-COPY-01-R1 第二版，I 10-09）——
 * full：详情页，完整的一节，最后有“TA 在旅途中、在聊天里给你的照片……”那一句；
 * brief：上传处原地打开的面板，省掉旅途那一句（面板只讲你放上来的这张照片），其余逐字一样。
 */
function PhotoSection({ variant }: { variant: "full" | "brief" }) {
  return (
    <Card data-testid="data-use-photo">
      <h2 className="ps-section-title">照片</h2>
      <p>{PHOTO_DATA_USE}</p>
      <p>{PHOTO_SCOPE_NOTE}</p>
      <ul className="ps-data-use__list" aria-label="照片用在这些地方">
        {PHOTO_SURFACES.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
      {variant === "full" ? <p>{PHOTO_TRIP_NOTE}</p> : null}
    </Card>
  );
}

/**
 * 目前在用的外部服务：服务名只出现在这里，写成“目前在用：<名字>”。
 * only：只列这几种用途（面板里只要图像两种）；不给就列全部。读同一份查询（同一个键、同一个请求），规则都一样。
 */
function InUseServices({ only }: { only?: readonly DataServicePurpose[] }) {
  const { session } = useServices();
  const query = useQuery({ queryKey: DATA_SERVICES_KEY, queryFn: () => session.dataServices(), staleTime: 5 * 60_000 });
  const rows = query.data === undefined ? undefined : readDataServices(query.data, only);
  // 接口答了、要列的用途一个都没有（或里面的用途都不认识）：整块不显示，不说“没有在用的服务”（6c2b 10-09 定）
  if (rows && rows.length === 0) return null;
  return (
    <Card className="ps-data-use__in-use">
      <h2 className="ps-section-title">{SERVICES_TITLE}</h2>
      {query.isPending ? (
        <p role="status">正在读取…</p>
      ) : query.isError || rows === null || rows === undefined ? (
        <div className="ps-data-use__unreadable">
          <p role="status">{SERVICES_UNREADABLE}</p>
          <Button variant="secondary" loading={query.isFetching} onClick={() => void query.refetch()}>
            再读一次
          </Button>
        </div>
      ) : (
        <ul className="ps-data-use__services">
          {rows.map((row) => (
            <li key={`${row.purpose}:${row.text}`} data-purpose={row.purpose}>
              <strong>{row.label}</strong>
              <span>{row.text}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
